import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '@environment/environment';
import { LoggerService } from '@app/core/infrastructure';
import { AnalyticsQueryParams, AnalyticsTrendQueryParams } from '../analytics-shared';
import { RemindersOverview, RemindersTrendPoint, MedicineTrackerResponse, TrackerQueryParams } from './reminders-analytics-api.models';

/**
 * Pure API service for reminders analytics HTTP operations
 * No state management — just HTTP calls
 */
@Injectable({
  providedIn: 'root'
})
export class RemindersAnalyticsApiService {
  private readonly logger = inject(LoggerService).scope('RemindersAnalyticsApiService');

  private readonly baseUrl = `${environment.API_BASE_URL}/protected/care/analytics/reminders`;

  constructor(private http: HttpClient) {
    this.logger.debug('Initialized');
  }

  /**
   * GET /protected/care/analytics/reminders/overview
   * Aggregated reminder metrics for a date range with per-category breakdown.
   */
  public getOverview(params: AnalyticsQueryParams): Observable<RemindersOverview> {
    const httpParams = new HttpParams()
      .set('user_id', params.user_id)
      .set('start_date', params.start_date)
      .set('end_date', params.end_date);

    return this.http.get<RemindersOverview>(`${this.baseUrl}/overview`, { params: httpParams }).pipe(
      catchError(error => {
        this.logger.error('Error fetching reminders overview:', error);
        return throwError(() => new Error('Failed to fetch reminders overview'));
      })
    );
  }

  /**
   * GET /protected/care/analytics/reminders/trend
   * Time-bucketed completion rates for chart visualization.
   * bucket_size defaults to 'week' on the backend if not provided.
   */
  public getTrend(params: AnalyticsTrendQueryParams): Observable<RemindersTrendPoint[]> {
    let httpParams = new HttpParams()
      .set('user_id', params.user_id)
      .set('start_date', params.start_date)
      .set('end_date', params.end_date);

    if (params.bucket_size) {
      httpParams = httpParams.set('bucket_size', params.bucket_size);
    }

    return this.http.get<RemindersTrendPoint[]>(`${this.baseUrl}/trend`, { params: httpParams }).pipe(
      catchError(error => {
        this.logger.error('Error fetching reminders trend:', error);
        return throwError(() => new Error('Failed to fetch reminders trend'));
      })
    );
  }

  /**
   * GET /protected/care/analytics/reminders/tracker
   * Per-reminder per-day status for medicine tracker calendar.
   * category defaults to 'medication' on the backend if not provided.
   */
  public getTracker(params: TrackerQueryParams): Observable<MedicineTrackerResponse> {
    let httpParams = new HttpParams()
      .set('user_id', params.user_id)
      .set('start_date', params.start_date)
      .set('end_date', params.end_date);

    if (params.category) {
      httpParams = httpParams.set('category', params.category);
    }

    return this.http.get<MedicineTrackerResponse>(`${this.baseUrl}/tracker`, { params: httpParams }).pipe(
      catchError(error => {
        this.logger.error('Error fetching medicine tracker:', error);
        return throwError(() => new Error('Failed to fetch medicine tracker'));
      })
    );
  }
}
